import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { update_status } from '../../deals/deals-subcomponents/status-and-pipeline-dates/graphql/statusesAndSequences';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import moment from 'moment';
import { MissingDataService } from '../../services/missing-data.service';
import { MatTableDataSource } from '@angular/material/table';

const UPDATE_STATUS = update_status;

export interface StatusAndDatesTable {
  index: number;
  pmid: string;
  status: string;
  statusName: string | null;
  dates: any;
}

@Component({
    selector: 'app-timeline',
    templateUrl: './timeline.component.html',
    styleUrls: ['./timeline.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TimelineComponent implements OnInit {
  @Input() status_and_dates: any;
  @Input() dispositions: boolean;
  @Input() permissions: any;
  @Input() dateDelete: any;
  @Input() hasRequirements: boolean;
  @Input() isMobile: boolean;
  @Input() projectSubstatusDates: any[];
  @Input() projectSubstatuses: any[];
  @Input() subsequences: any[];
  @Input() projectCredits: any[];

  isEditMode: boolean;
  @Input('isEditMode') set _isEditMode(value: boolean) {
    if (!value && this.isEditMode) {
      this.dateChange.emit(this.status_and_dates);
    }
    this.isEditMode = value;
  }

  @Output() dateChange: EventEmitter<any> = new EventEmitter();
  @Output() deleteDate: EventEmitter<any> = new EventEmitter();
  @Output() deleteForecastDate: EventEmitter<any> = new EventEmitter();
  @Output() hasMissingData: EventEmitter<any> = new EventEmitter();
  @Output() statusChange: EventEmitter<any> = new EventEmitter();
  @Output() projectSubstatusDatesChange: EventEmitter<any[]> =
    new EventEmitter();
  @Output() projectSubstatusesChange: EventEmitter<any[]> = new EventEmitter();

  statusAndDatesDataSource: MatTableDataSource<StatusAndDatesTable>;
  statusAndDatesColumns: string[] = ['status', 'date'];

  sequencePermissions: boolean = false;
  validationBypass: boolean = false;
  acqInitialBypass: boolean = false;
  currentStatus: string | null;
  currentStatusName: string = 'None';
  saving: boolean = false;

  constructor(
    private apollo: Apollo,
    private _snackBar: MatSnackBar,
    private missingData: MissingDataService
  ) {}

  ngOnInit() {
    if (this.status_and_dates.sequence) {
      if (this.status_and_dates.sequence.name === 'Closing') {
        this.sequencePermissions =
          this.permissions.closingStatusSequencePermissions._edit;
      } else if (this.status_and_dates.sequence.name === 'Managing') {
        this.sequencePermissions =
          this.permissions.managingStatusSequencePermissions._edit;
      } else if (this.status_and_dates.sequence.name === 'Syndications') {
        this.sequencePermissions =
          this.permissions.syndicationsStatusSequencePermissions._edit;
      }
      this.statusAndDatesDataSource =
        new MatTableDataSource<StatusAndDatesTable>(
          this.status_and_dates.statusDates
        );
    }

    for (let i = 0; i < this.permissions.groupsFlattened.length; i++) {
      if (this.permissions.groupsFlattened[i].name === 'Pipeline Project Date Editors' || this.permissions.groupsFlattened[i].name === 'Account Managers') {
        this.validationBypass = true;
        break;
      }
    }

    for (let i = 0; i < this.permissions.groupsFlattened.length; i++) {
      if (this.permissions.groupsFlattened[i].name === 'Acquisitions') {
        this.acqInitialBypass = true;
        break;
      }
    }

    if (this.status_and_dates.activeStatusIndex) {
      for (let i = 0; i < this.status_and_dates.sequence.statuses.length; i++) {
        if (
          this.status_and_dates.sequence.statuses[i].index ===
          this.status_and_dates.activeStatusIndex
        ) {
          this.currentStatus =
            this.status_and_dates.sequence.statuses[i].status.id;
          this.currentStatusName =
            this.status_and_dates.sequence.statuses[i].status.name;
        }
      }
    }
  }

  hasInitialValidation(initialDate: string): boolean {
    let isVal = false;
    if (this.validationBypass) {
      isVal = true;
    } else if (this.acqInitialBypass) {
      isVal = initialDate === '';
    }
    return isVal;
  }

  changeStatus(status: any) {
    //If the user has permissions, the deal isn't dead, the status sequence isn't dispositions,
    //the status sequence is active, and the status doesn't equal the current status:

    //Then allow the user to change the status on the status sequence
    if (
      this.sequencePermissions &&
      !this.status_and_dates.isDead &&
      !this.dispositions &&
      status.index !== this.status_and_dates.activeStatusIndex &&
      this.status_and_dates.sequence.active &&
      !this.saving
    ) {
      this.saving = true;
      this.apollo
        .mutate({
          mutation: UPDATE_STATUS,
          variables: {
            id: this.status_and_dates.sequence.projectStatusID,
            status_id: status.status.id,
            status_sequence_id: this.status_and_dates.sequence.id,
          },
        })
        .subscribe(
          ({ data }) => {
            this.saving = false;
            this.isEditMode = false;
            let { updateStatus } = data as {
              updateStatus: {
                project: any;
                projectSubstatuses: any[];
                projectSubstatusDates: any[];
              };
            };
            let { project, projectSubstatuses, projectSubstatusDates } =
              updateStatus;
            // @ts-ignore
            this.statusChange.emit(project);
            this.currentStatus = status.status.id;
            this.currentStatusName = status.status.name;
            this.status_and_dates.activeStatusIndex = status.index;
            this.hasMissingData.emit(false);
            this.missingData.setData(null);
            if (projectSubstatuses) {
              this.projectSubstatuses = projectSubstatuses;
              this.projectSubstatusesChange.emit(projectSubstatuses);
            }
            if (projectSubstatusDates) {
              this.projectSubstatusDates = projectSubstatusDates;
              this.projectSubstatusDatesChange.emit(projectSubstatusDates);
            }
            this.openSnackBar('Successfully updated status.');
          },
          (error) => {
            this.saving = false;
            if (this.missingData.getData() !== null) {
              this.hasMissingData.emit(true);
            }
          }
        );
    }
  }

  disableTargetCheckBox(dateObj: any) {
    if (!dateObj?.newDate) {
      return false;
    } else {
      let today = moment.utc();
      let momentDate = moment.utc(dateObj.newDate);
      return momentDate.isAfter(today) && dateObj.newIsTarget;
    }
  }

  changeDate(event: any, dates: any, index: number, isInitial: boolean) {
    let newVal = null;
    if (event.value !== null) {
      newVal = new Date(
        moment.utc(event.value).format('MM/DD/YYYY')
      ).toISOString();
    }
    let key = index;
    if (isInitial) {
      this.status_and_dates.statusDates[key].dates.newInitialDate = newVal;
    } else {
      this.status_and_dates.statusDates[key].dates.newDate = newVal;
      let today = moment.utc();
      let momentDate = moment.utc(newVal);
      if (momentDate.isAfter(today)) {
        this.status_and_dates.statusDates[key].dates.newIsTarget = true;
      }
    }
  }
  substatusChange() {}
  changeForecastDate(event: any, dates: any, key: number) {
    let newVal = null;
    if (event.value !== null) {
      newVal = new Date(
        moment.utc(event.value).format('MM/DD/YYYY')
      ).toISOString();
    }
    this.status_and_dates.statusDates[key].forecastDates.newDate = newVal;
  }

  deleteDateConfirmation(date: any, isInitial: boolean) {
    this.dateDelete = date;
    this.dateDelete.isInitial = isInitial;
    this.deleteDate.emit(date);
  }

  deleteForecastDateConfirmation(date: any) {
    this.dateDelete = date;
    this.deleteForecastDate.emit(date);
  }

  openSnackBar(message: string) {
    const config = new MatSnackBarConfig();
    config.panelClass = ['save-success'];
    config.horizontalPosition = 'center';
    config.verticalPosition = 'top';
    config.duration = 3000;
    this._snackBar.open(message, 'Ok', config);
  }
}
